import { GameState, Phase, BottleId } from "../core/types";

export type GameStateEvent = 
  | { type: "level_start"; level: number }
  | { type: "bottle_selected"; bottleId: BottleId }
  | { type: "bottle_deselected" }
  | { type: "pour_start"; from: BottleId; to: BottleId }
  | { type: "pour_complete"; from: BottleId; to: BottleId }
  | { type: "seal_start"; bottleId: BottleId }
  | { type: "seal_complete"; bottleId: BottleId }
  | { type: "pack_start"; bottleId: BottleId; slotIndex: number }
  | { type: "pack_complete"; bottleId: BottleId; slotIndex: number }
  | { type: "undo" }
  | { type: "restart" }
  | { type: "hint" }
  | { type: "win" }
  | { type: "lose" }
  | { type: "pause" }
  | { type: "resume" };

export interface StateTransition {
  from: Phase;
  event: GameStateEvent["type"];
  to: Phase;
  guard?: (event: GameStateEvent, state: GameState) => boolean;
  action?: (event: GameStateEvent, state: GameState) => void;
}

export class GameStateMachine {
  private state: GameState;
  private currentPhase: Phase = "idle";
  private selectedBottleId: BottleId | null = null;
  private transitions: Map<string, StateTransition[]> = new Map();
  private eventListeners: Map<GameStateEvent["type"], Set<(event: GameStateEvent) => void>> = new Map();

  constructor(initialState: GameState) {
    this.state = initialState;
    this.setupTransitions();
  }

  private setupTransitions(): void {
    this.addTransition("idle", "bottle_selected", "selectSource");
    this.addTransition("idle", "undo", "idle");
    this.addTransition("idle", "restart", "idle");
    this.addTransition("idle", "hint", "hint");
    this.addTransition("idle", "pause", "idle");

    this.addTransition("selectSource", "bottle_selected", "selectSource");
    this.addTransition("selectSource", "bottle_deselected", "idle");
    this.addTransition("selectSource", "pour_start", "pouring");
    this.addTransition("selectSource", "undo", "idle");
    this.addTransition("selectSource", "restart", "idle");
    this.addTransition("selectSource", "pause", "selectSource");

    this.addTransition("pouring", "pour_complete", "idle");
    this.addTransition("pouring", "seal_start", "sealing");
    this.addTransition("pouring", "pause", "pouring");

    this.addTransition("sealing", "seal_complete", "packing");
    this.addTransition("sealing", "pause", "sealing");

    this.addTransition("packing", "pack_complete", "idle");
    this.addTransition("packing", "pause", "packing");

    this.addTransition("hint", "bottle_selected", "selectSource");
    this.addTransition("hint", "undo", "idle");
    this.addTransition("hint", "restart", "idle");
    this.addTransition("hint", "pause", "hint");

    // Transition to win phase
    this.addTransition("idle", "win", "win");
    this.addTransition("pouring", "win", "win");
    this.addTransition("packing", "win", "win");

    this.addTransition("win", "restart", "idle");
    this.addTransition("win", "undo", "idle");

    this.addTransition("lose", "restart", "idle");
    this.addTransition("lose", "undo", "idle");
  }

  private addTransition(from: Phase, event: GameStateEvent["type"], to: Phase, guard?: (event: GameStateEvent, state: GameState) => boolean, action?: (event: GameStateEvent, state: GameState) => void): void {
    const key = `${from}:${event}`;
    if (!this.transitions.has(key)) {
      this.transitions.set(key, []);
    }
    this.transitions.get(key)!.push({ from, event, to, guard, action });
  }

  dispatch(event: GameStateEvent): boolean {
    const key = `${this.currentPhase}:${event.type}`;
    const possibleTransitions = this.transitions.get(key);

    if (!possibleTransitions || possibleTransitions.length === 0) {
      console.warn(`No transition found for phase ${this.currentPhase} and event ${event.type}`);
      return false;
    }

    for (const transition of possibleTransitions) {
      if (transition.guard && !transition.guard(event, this.state)) {
        continue;
      }


      this.currentPhase = transition.to;

      if (transition.action) {
        transition.action(event, this.state);
      }

      this.state.phase = this.currentPhase;

      this.notifyListeners(event);

      return true;
    }

    console.warn(`Guard failed for all transitions from ${this.currentPhase} on event ${event.type}`);
    return false;
  }

  on(eventType: GameStateEvent["type"], callback: (event: GameStateEvent) => void): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }
    this.eventListeners.get(eventType)!.add(callback);
  }

  off(eventType: GameStateEvent["type"], callback: (event: GameStateEvent) => void): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  private notifyListeners(event: GameStateEvent): void {
    const listeners = this.eventListeners.get(event.type);
    if (listeners) {
      listeners.forEach(callback => callback(event));
    }
  }

  getCurrentPhase(): Phase {
    return this.currentPhase;
  }

  getState(): GameState {
    return this.state;
  }

  setState(newState: GameState): void {
    this.state = newState;
    this.currentPhase = newState.phase;
  }

  getSelectedBottleId(): BottleId | null {
    return this.selectedBottleId;
  }

  setSelectedBottleId(bottleId: BottleId | null): void {
    this.selectedBottleId = bottleId;
  }

  reset(state: GameState): void {
    this.state = state;
    this.currentPhase = state.phase;
    this.selectedBottleId = null;
  }
}
